import { isNativeShell, shell } from "@/lib/platform";

/*
 * Handing a file to the user, whichever shell they are in: a browser download, the Windows
 * save dialog, or the Android share sheet (which also opens the PDF in their viewer).
 */

const REVOKE_AFTER_MS = 60_000;

function downloadInBrowser(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
}

async function base64Of(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/** Write the file into the app's cache, then offer it to WhatsApp, Drive, a viewer... */
async function shareOnAndroid(blob: Blob, filename: string): Promise<void> {
  const [{ Directory, Filesystem }, { Share }] = await Promise.all([
    import("@capacitor/filesystem"),
    import("@capacitor/share"),
  ]);
  const written = await Filesystem.writeFile({
    path: filename,
    data: await base64Of(blob),
    directory: Directory.Cache,
  });
  await Share.share({ title: filename, url: written.uri });
}

/** A name that is safe as a single path segment: no separators, no control characters. */
export function safeFilename(name: string): string {
  const cleaned = name.replace(/[\\/\x00-\x1f\x7f]/g, "_").trim();
  return cleaned === "" || cleaned === "." || cleaned === ".." ? "attachment" : cleaned;
}

/** Save a file under `filename` in whichever way the current shell allows. */
export async function saveBlob(blob: Blob, rawFilename: string): Promise<void> {
  const filename = safeFilename(rawFilename);
  const current = shell();
  if (current === "android") {
    await shareOnAndroid(blob, filename);
    return;
  }
  if (current === "desktop" && window.pboDesktop) {
    await window.pboDesktop.saveFile(filename, await blob.arrayBuffer());
    return;
  }
  downloadInBrowser(blob, filename);
}

/**
 * Show a PDF. In a browser the tab is opened before the download starts, so pop-up blockers
 * allow it; in the apps the file is handed to the system instead, which opens a viewer.
 */
export async function openPdf(load: () => Promise<Blob>, filename: string): Promise<void> {
  if (isNativeShell()) {
    await saveBlob(await load(), filename);
    return;
  }
  const tab = window.open("", "_blank");
  try {
    const blob = await load();
    if (tab === null) {
      downloadInBrowser(blob, filename);
      return;
    }
    const url = URL.createObjectURL(blob);
    tab.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
  } catch (error) {
    tab?.close();
    throw error;
  }
}
